import React, { useState, useRef } from "react";
import {
  ChevronRight, ChevronLeft, CheckCircle, FileText, Shield,
  Upload, X, Plus, AlertCircle, Clock, Eye, Edit3, MessageSquare,
  Paperclip, HelpCircle, Check, ArrowLeft, ExternalLink, Wrench
} from "lucide-react";
import { detectLocale } from "../i18n";

const tsData = {
  en: {
    overline: "TECHNICAL SUPPORT",
    title: "Open technical request",
    subtitle: "Describe the problem with as much precision as possible to speed up triage and diagnosis.",
    helpText: "Before continuing, make sure you have the basic product and issue details. This helps accelerate the analysis.",
    checklist: ["Serial number", "Proof of purchase", "Photos or video of the issue", "Contact details"],
    ctaPrimary: "Continue",
    ctaSecondary: "Back to support",
    stepLabel: "Step", of: "of", back: "Back", next: "Continue", submit: "Submit request",
    steps: ["Start", "Eligibility", "Product", "Issue", "Upload", "Review", "Done"],
    // Step 1 — Eligibility / Pre-triage
    s1_title: "Technical eligibility check",
    s1_text: "Let's confirm the case is technical so we can route it correctly.",
    s1_q1: "What type of issue are you experiencing?",
    s1_q1_a1: "Technical / malfunction", s1_q1_a2: "Warranty claim", s1_q1_a3: "Parts request", s1_q1_a4: "General question",
    s1_q2: "Is the product currently unusable or unsafe?",
    s1_q2_a1: "Yes, it is unusable", s1_q2_a2: "No, it still works partially", s1_q2_a3: "It is a doubt / preventive",
    s1_redirect_warranty: "Your case seems more suited for warranty. You can also continue here if you prefer.",
    s1_redirect_parts: "For parts requests, visit our parts section or continue here for technical guidance.",
    s1_redirect_general: "For general questions, try our FAQ or contact support directly.",
    s1_continue_anyway: "You can continue with the technical request even if the case is not strictly technical.",
    // Step 2 — Product identification
    s2_title: "Product identification",
    s2_text: "Provide the product details so we can identify the correct coverage.",
    s2_type: "Product type",
    s2_type_ph: "Select type",
    s2_type_opts: ["E-Foil", "Jetboard", "Accessory", "Battery", "Charger", "Other"],
    s2_model: "Product model", s2_model_ph: "e.g. SharkX Pro",
    s2_serial: "Serial number", s2_serial_ph: "e.g. SX-2025-001234",
    s2_variant: "Variant / configuration", s2_variant_ph: "e.g. Full Carbon",
    s2_purchase_date: "Purchase date", s2_purchase_loc: "Purchase location", s2_purchase_loc_ph: "e.g. SharkX official store",
    s2_help: "The serial number helps speed up the analysis.",
    // Step 3 — Issue description
    s3_title: "Describe the issue",
    s3_text: "Explain what happened as objectively as possible.",
    s3_category: "Issue category",
    s3_cat_opts: ["Won't turn on", "Battery", "Remote / controller", "Structure", "Charger", "Noise", "Vibration", "Physical damage", "Performance loss", "Other"],
    s3_desc: "Describe what happened", s3_desc_ph: "Be as specific as possible. Include error messages, LED behavior, unusual sounds, etc.",
    s3_when: "When did the issue start?",
    s3_context: "When did it happen?",
    s3_ctx_opts: ["During use", "Transport", "Storage", "Charging", "After update", "Other"],
    s3_partial: "Does the product still work partially?",
    s3_partial_yes: "Yes, partially", s3_partial_no: "No", s3_partial_na: "Not sure",
    s3_frequency: "Is the issue intermittent or constant?",
    s3_freq_int: "Intermittent", s3_freq_const: "Constant",
    s3_example1: "Example: the remote won't connect after the last charge.",
    s3_example2: "Example: the battery won't charge and the LED keeps blinking.",
    s3_tried: "Have you already tried any solution?",
    s3_tried_ph: "e.g. restarted, recharged, reset, firmware update...",
    // Step 4 — Upload evidence
    s4_title: "Attach evidence",
    s4_text: "Images and videos help speed up the evaluation.",
    s4_product: "Product photo",
    s4_serial: "Serial number photo",
    s4_defect: "Defect photo",
    s4_video: "Issue video",
    s4_receipt: "Proof of purchase",
    s4_other: "Other relevant files",
    s4_formats: "Accepted: JPG, PNG, MP4, PDF. Max 20 MB per file.",
    s4_help: "Send clear images of the defect, serial number, and proof of purchase.",
    s4_drop: "Click or drag files here",
    s4_uploading: "Uploading...",
    s4_remove: "Remove",
    // Step 5 — Review
    s5_title: "Review before sending",
    s5_text: "Check the information before finalizing. After submission, you will receive a protocol number to track your case.",
    s5_eligibility: "Eligibility", s5_product: "Product", s5_serial: "Serial number",
    s5_issue: "Issue description", s5_attachments: "Attachments", s5_notes: "Notes",
    s5_type: "Type", s5_model: "Model", s5_purchase: "Purchase",
    s5_category: "Category", s5_desc: "Description", s5_when: "When it started",
    s5_context: "Context", s5_partial: "Partial operation", s5_frequency: "Frequency",
    s5_tried: "Solutions tried",
    s5_terms: "I declare that the information provided is true and I agree with the applicable privacy policy.",
    s5_edit: "Edit", s5_send: "Submit request",
    // Step 6 — Confirmation
    s6_title: "Request submitted successfully",
    s6_text: "Your request has been created. Save this protocol number to track your case.",
    s6_protocol: "Protocol number",
    s6_status: "Current status", s6_status_val: "Received",
    s6_summary: "Request summary",
    s6_eta: "Estimated response time", s6_eta_val: "2-5 business days",
    s6_channel: "Tracking channel",
    s6_view: "Track request", s6_new: "Open another request", s6_support: "Back to support", s6_faq: "Explore FAQ",
    s6_secondary: "Our team will review the information and respond through the provided channel.",
    // Tracking
    t_title: "Technical request tracking",
    t_not_found: "Request not found. Check the URL or contact support.",
    t_timeline: "Timeline", t_messages: "Messages", t_documents: "Pending documents",
    t_eta: "Estimated response", t_updated: "Last update",
    t_status_opts: ["Received", "Under review", "Awaiting documents", "Technical evaluation", "Resolved", "Rejected", "Completed", "Cancelled"],
    t_add_doc: "Send additional document", t_reply: "Reply to ticket", t_talk: "Contact support",
    t_support_msg: "If any information is missing, you can supplement the request from this screen.",
  },
  pt: {
    overline: "SUPORTE TÉCNICO",
    title: "Abrir chamado técnico",
    subtitle: "Descreva o problema com o máximo de precisão para agilizar a triagem e o diagnóstico.",
    helpText: "Antes de continuar, confirme se você tem os dados básicos do produto e do problema. Isso ajuda a acelerar a análise.",
    checklist: ["Número de série", "Comprovante de compra", "Fotos ou vídeo do problema", "Dados de contato"],
    ctaPrimary: "Continuar",
    ctaSecondary: "Voltar ao suporte",
    stepLabel: "Etapa", of: "de", back: "Voltar", next: "Continuar", submit: "Enviar solicitação",
    steps: ["Início", "Elegibilidade", "Produto", "Problema", "Anexos", "Revisão", "Enviado"],
    s1_title: "Verificação de elegibilidade técnica",
    s1_text: "Vamos confirmar se o caso é técnico para direcionar corretamente.",
    s1_q1: "Que tipo de problema você está enfrentando?",
    s1_q1_a1: "Técnico / defeito", s1_q1_a2: "Reclamação de garantia", s1_q1_a3: "Solicitação de peça", s1_q1_a4: "Dúvida geral",
    s1_q2: "O produto está inutilizável ou inseguro?",
    s1_q2_a1: "Sim, está inutilizável", s1_q2_a2: "Não, ainda funciona parcialmente", s1_q2_a3: "É uma dúvida / preventivo",
    s1_redirect_warranty: "Seu caso parece mais adequado para garantia. Você também pode continuar aqui se preferir.",
    s1_redirect_parts: "Para solicitação de peças, visite a seção de peças ou continue aqui para orientação técnica.",
    s1_redirect_general: "Para dúvidas gerais, consulte nosso FAQ ou entre em contato com o suporte diretamente.",
    s1_continue_anyway: "Você pode continuar com o chamado técnico mesmo que o caso não seja estritamente técnico.",
    s2_title: "Identificação do produto",
    s2_text: "Informe o produto para que possamos identificar a cobertura correta.",
    s2_type: "Tipo de produto",
    s2_type_ph: "Selecione o tipo",
    s2_type_opts: ["E-Foil", "Jetboard", "Acessório", "Bateria", "Carregador", "Outro"],
    s2_model: "Modelo do produto", s2_model_ph: "Ex: SharkX Pro",
    s2_serial: "Número de série", s2_serial_ph: "Ex: SX-2025-001234",
    s2_variant: "Variante / configuração", s2_variant_ph: "Ex: Full Carbon",
    s2_purchase_date: "Data da compra", s2_purchase_loc: "Local de compra", s2_purchase_loc_ph: "Ex: Loja oficial SharkX",
    s2_help: "O número de série ajuda a acelerar a análise.",
    s3_title: "Descreva o problema",
    s3_text: "Explique o que aconteceu da forma mais objetiva possível.",
    s3_category: "Categoria do problema",
    s3_cat_opts: ["Não liga", "Bateria", "Controle", "Estrutura", "Carregador", "Ruído", "Vibração", "Dano físico", "Perda de performance", "Outro"],
    s3_desc: "Descreva o que aconteceu", s3_desc_ph: "Seja o mais específico possível. Inclua mensagens de erro, comportamento do LED, sons incomuns, etc.",
    s3_when: "Quando o problema começou?",
    s3_context: "Aconteceu durante?",
    s3_ctx_opts: ["Uso", "Transporte", "Armazenamento", "Carregamento", "Após atualização", "Outro"],
    s3_partial: "O produto ainda funciona parcialmente?",
    s3_partial_yes: "Sim, parcialmente", s3_partial_no: "Não", s3_partial_na: "Não sei",
    s3_frequency: "O problema é intermitente ou constante?",
    s3_freq_int: "Intermitente", s3_freq_const: "Constante",
    s3_example1: "Exemplo: o controle não conecta após a última carga.",
    s3_example2: "Exemplo: a bateria não carrega e o LED fica piscando.",
    s3_tried: "Você já tentou alguma solução?",
    s3_tried_ph: "Ex: reiniciou, recarregou, resetou, atualizou firmware...",
    s4_title: "Anexe evidências",
    s4_text: "As imagens e vídeos ajudam a acelerar a avaliação.",
    s4_product: "Foto do produto",
    s4_serial: "Foto do número de série",
    s4_defect: "Foto do defeito",
    s4_video: "Vídeo do problema",
    s4_receipt: "Comprovante de compra",
    s4_other: "Outros arquivos relevantes",
    s4_formats: "Aceitos: JPG, PNG, MP4, PDF. Máx 20 MB por arquivo.",
    s4_help: "Envie imagens nítidas do defeito, do número de série e do comprovante de compra.",
    s4_drop: "Clique ou arraste arquivos aqui",
    s4_uploading: "Enviando...",
    s4_remove: "Remover",
    s5_title: "Revise antes de enviar",
    s5_text: "Confira as informações antes de finalizar. Depois do envio, você receberá um número de protocolo para acompanhar o caso.",
    s5_eligibility: "Elegibilidade", s5_product: "Produto", s5_serial: "Número de série",
    s5_issue: "Descrição do problema", s5_attachments: "Anexos", s5_notes: "Observações",
    s5_type: "Tipo", s5_model: "Modelo", s5_purchase: "Compra",
    s5_category: "Categoria", s5_desc: "Descrição", s5_when: "Quando começou",
    s5_context: "Contexto", s5_partial: "Funcionamento parcial", s5_frequency: "Frequência",
    s5_tried: "Soluções tentadas",
    s5_terms: "Declaro que as informações enviadas são verdadeiras e concordo com a política de privacidade aplicável.",
    s5_edit: "Editar", s5_send: "Enviar solicitação",
    s6_title: "Solicitação enviada com sucesso",
    s6_text: "Sua solicitação foi criada. Guarde este número de protocolo para acompanhar o atendimento.",
    s6_protocol: "Número do protocolo",
    s6_status: "Status atual", s6_status_val: "Recebido",
    s6_summary: "Resumo do pedido",
    s6_eta: "Prazo estimado de retorno", s6_eta_val: "2-5 dias úteis",
    s6_channel: "Canal de acompanhamento",
    s6_view: "Acompanhar chamado", s6_new: "Abrir outro chamado", s6_support: "Voltar para suporte", s6_faq: "Explorar FAQ",
    s6_secondary: "Nossa equipe vai analisar as informações e retornar pelo canal informado.",
    t_title: "Acompanhamento de chamado técnico",
    t_not_found: "Solicitação não encontrada. Verifique a URL ou entre em contato com o suporte.",
    t_timeline: "Linha do tempo", t_messages: "Mensagens", t_documents: "Documentos pendentes",
    t_eta: "Prazo estimado", t_updated: "Última atualização",
    t_status_opts: ["Recebido", "Em análise", "Aguardando documentos", "Em avaliação técnica", "Resolvido", "Rejeitado", "Concluído", "Cancelado"],
    t_add_doc: "Enviar documento adicional", t_reply: "Responder ao chamado", t_talk: "Falar com suporte",
    t_support_msg: "Se faltar alguma informação, você poderá complementar o chamado por esta tela.",
  },
  es: {
    overline: "SOPORTE TÉCNICO",
    title: "Abrir llamado técnico",
    subtitle: "Describa el problema con la mayor precisión posible para agilizar la triage y el diagnóstico.",
    helpText: "Antes de continuar, asegúrese de tener los datos básicos del producto y del problema. Esto ayuda a acelerar el análisis.",
    checklist: ["Número de serie", "Comprobante de compra", "Fotos o video del problema", "Datos de contacto"],
    ctaPrimary: "Continuar",
    ctaSecondary: "Volver al soporte",
    stepLabel: "Paso", of: "de", back: "Volver", next: "Continuar", submit: "Enviar solicitud",
    steps: ["Inicio", "Elegibilidad", "Producto", "Problema", "Adjuntos", "Revisión", "Enviado"],
    s1_title: "Verificación de elegibilidad técnica",
    s1_text: "Vamos a confirmar si el caso es técnico para dirigirlo correctamente.",
    s1_q1: "¿Qué tipo de problema está experimentando?",
    s1_q1_a1: "Técnico / falla", s1_q1_a2: "Reclamo de garantía", s1_q1_a3: "Solicitud de pieza", s1_q1_a4: "Duda general",
    s1_q2: "¿El producto está actualmente inutilizable o inseguro?",
    s1_q2_a1: "Sí, está inutilizable", s1_q2_a2: "No, aún funciona parcialmente", s1_q2_a3: "Es una duda / preventivo",
    s1_redirect_warranty: "Su caso parece más adecuado para garantía. También puede continuar aquí si lo prefiere.",
    s1_redirect_parts: "Para solicitudes de piezas, visite la sección de piezas o continúe aquí para orientación técnica.",
    s1_redirect_general: "Para dudas generales, consulte nuestro FAQ o contacte soporte directamente.",
    s1_continue_anyway: "Puede continuar con el llamado técnico incluso si el caso no es estrictamente técnico.",
    s2_title: "Identificación del producto",
    s2_text: "Informe el producto para que podamos identificar la cobertura correcta.",
    s2_type: "Tipo de producto",
    s2_type_ph: "Seleccione el tipo",
    s2_type_opts: ["E-Foil", "Jetboard", "Accesorio", "Batería", "Cargador", "Otro"],
    s2_model: "Modelo del producto", s2_model_ph: "Ej: SharkX Pro",
    s2_serial: "Número de serie", s2_serial_ph: "Ej: SX-2025-001234",
    s2_variant: "Variante / configuración", s2_variant_ph: "Ej: Full Carbon",
    s2_purchase_date: "Fecha de compra", s2_purchase_loc: "Lugar de compra", s2_purchase_loc_ph: "Ej: Tienda oficial SharkX",
    s2_help: "El número de serie ayuda a acelerar el análisis.",
    s3_title: "Describa el problema",
    s3_text: "Explique lo que pasó de la forma más objetiva posible.",
    s3_category: "Categoría del problema",
    s3_cat_opts: ["No enciende", "Batería", "Control", "Estructura", "Cargador", "Ruido", "Vibración", "Daño físico", "Pérdida de rendimiento", "Otro"],
    s3_desc: "Describa lo que pasó", s3_desc_ph: "Sea lo más específico posible. Incluya mensajes de error, comportamiento del LED, sonidos inusuales, etc.",
    s3_when: "¿Cuándo empezó el problema?",
    s3_context: "¿Cuándo ocurrió?",
    s3_ctx_opts: ["Durante el uso", "Transporte", "Almacenamiento", "Carga", "Después de actualización", "Otro"],
    s3_partial: "¿El producto aún funciona parcialmente?",
    s3_partial_yes: "Sí, parcialmente", s3_partial_no: "No", s3_partial_na: "No estoy seguro",
    s3_frequency: "¿El problema es intermitente o constante?",
    s3_freq_int: "Intermitente", s3_freq_const: "Constante",
    s3_example1: "Ejemplo: el control no conecta después de la última carga.",
    s3_example2: "Ejemplo: la batería no carga y el LED parpadea.",
    s3_tried: "¿Ya intentó alguna solución?",
    s3_tried_ph: "Ej: reinició, recargó, reseteó, actualizó firmware...",
    s4_title: "Adjunte evidencias",
    s4_text: "Las imágenes y videos ayudan a acelerar la evaluación.",
    s4_product: "Foto del producto",
    s4_serial: "Foto del número de serie",
    s4_defect: "Foto del defecto",
    s4_video: "Video del problema",
    s4_receipt: "Comprobante de compra",
    s4_other: "Otros archivos relevantes",
    s4_formats: "Aceptados: JPG, PNG, MP4, PDF. Máx 20 MB por archivo.",
    s4_help: "Envíe imágenes claras del defecto, del número de serie y del comprobante de compra.",
    s4_drop: "Haga clic o arrastre archivos aquí",
    s4_uploading: "Enviando...",
    s4_remove: "Eliminar",
    s5_title: "Revise antes de enviar",
    s5_text: "Revise la información antes de finalizar. Después del envío, recibirá un número de protocolo para seguir su caso.",
    s5_eligibility: "Elegibilidad", s5_product: "Producto", s5_serial: "Número de serie",
    s5_issue: "Descripción del problema", s5_attachments: "Adjuntos", s5_notes: "Observaciones",
    s5_type: "Tipo", s5_model: "Modelo", s5_purchase: "Compra",
    s5_category: "Categoría", s5_desc: "Descripción", s5_when: "Cuándo empezó",
    s5_context: "Contexto", s5_partial: "Operación parcial", s5_frequency: "Frecuencia",
    s5_tried: "Soluciones intentadas",
    s5_terms: "Declaro que la información enviada es verdadera y acepto la política de privacidad aplicable.",
    s5_edit: "Editar", s5_send: "Enviar solicitud",
    s6_title: "Solicitud enviada con éxito",
    s6_text: "Su solicitud fue creada. Guarde este número de protocolo para seguir la atención.",
    s6_protocol: "Número de protocolo",
    s6_status: "Estado actual", s6_status_val: "Recibido",
    s6_summary: "Resumen de la solicitud",
    s6_eta: "Plazo estimado de respuesta", s6_eta_val: "2-5 días hábiles",
    s6_channel: "Canal de seguimiento",
    s6_view: "Seguir llamado", s6_new: "Abrir otro llamado", s6_support: "Volver a soporte", s6_faq: "Explorar FAQ",
    s6_secondary: "Nuestro equipo revisará la información y responderá por el canal indicado.",
    t_title: "Seguimiento de llamado técnico",
    t_not_found: "Solicitud no encontrada. Verifique la URL o contacte soporte.",
    t_timeline: "Línea de tiempo", t_messages: "Mensajes", t_documents: "Documentos pendientes",
    t_eta: "Plazo estimado", t_updated: "Última actualización",
    t_status_opts: ["Recibido", "En análisis", "Esperando documentos", "Evaluación técnica", "Resuelto", "Rechazado", "Completado", "Cancelado"],
    t_add_doc: "Enviar documento adicional", t_reply: "Responder al ticket", t_talk: "Hablar con soporte",
    t_support_msg: "Si falta alguna información, puede complementar la solicitud desde esta pantalla.",
  },
};

// Generate a protocol number
function generateProtocol() {
  const ts = Date.now().toString(36).toUpperCase();
  const rnd = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TS-${ts}-${rnd}`;
}

// File upload simulation
function simulateUpload(file, onProgress) {
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        resolve({ name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file) });
      }
      onProgress(Math.min(Math.round(progress), 100));
    }, 200);
  });
}

export default function TechnicalSupportRequest() {
  const locale = detectLocale();
  const t = tsData[locale] || tsData.en;
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = t.steps.length;
  const fileInputRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    // Step 1 — Eligibility
    q1: "", q2: "",
    // Step 2 — Product
    productType: "", model: "", serial: "", variant: "", purchaseDate: "", purchaseLoc: "",
    // Step 3 — Issue
    category: "", desc: "", when: "", context: "", partial: "", frequency: "", tried: "",
    // Step 5 — Review
    terms: false,
  });

  // Upload state
  const [uploads, setUploads] = useState({
    product: [], serial: [], defect: [], video: [], receipt: [], other: [],
  });
  const [uploading, setUploading] = useState({});

  // Confirmation state
  const [protocol, setProtocol] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const goNext = () => { if (currentStep < totalSteps - 1) setCurrentStep((s) => s + 1); };
  const goBack = () => { if (currentStep > 0) setCurrentStep((s) => s - 1); };
  const goToStep = (s) => { if (s >= 0 && s < totalSteps) setCurrentStep(s); };

  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  // Determine redirect suggestion for step 1
  const getRedirectSuggestion = () => {
    if (form.q1 === "warranty") return t.s1_redirect_warranty;
    if (form.q1 === "parts") return t.s1_redirect_parts;
    if (form.q1 === "general") return t.s1_redirect_general;
    return null;
  };

  // Handle file upload
  const handleFileUpload = async (category, files) => {
    for (const file of files) {
      const id = `${category}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
      setUploading((prev) => ({ ...prev, [id]: 0 }));
      const result = await simulateUpload(file, (p) => {
        setUploading((prev) => ({ ...prev, [id]: p }));
      });
      setUploads((prev) => ({
        ...prev,
        [category]: [...prev[category], { id, ...result, progress: 100 }],
      }));
      setUploading((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const removeFile = (category, fileId) => {
    setUploads((prev) => ({
      ...prev,
      [category]: prev[category].filter((f) => f.id !== fileId),
    }));
  };

  const totalUploaded = Object.values(uploads).flat().length;

  // Submit
  const handleSubmit = () => {
    const proto = generateProtocol();
    setProtocol(proto);
    setSubmitted(true);
    setCurrentStep(6);
    const requestData = {
      protocol: proto,
      form,
      uploads: Object.fromEntries(
        Object.entries(uploads).map(([k, v]) => [k, v.map((f) => ({ name: f.name, size: f.size }))])
      ),
      status: 0,
      createdAt: new Date().toISOString(),
      timeline: [
        { status: 0, date: new Date().toISOString(), text: t.s6_status_val },
      ],
    };
    const existing = JSON.parse(localStorage.getItem("sharkx_tech_requests") || "[]");
    existing.push(requestData);
    localStorage.setItem("sharkx_tech_requests", JSON.stringify(existing));
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors placeholder:text-slate-400";
  const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";
  const selectCls = "w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors appearance-none cursor-pointer";

  const redirectSuggestion = getRedirectSuggestion();

  // Helper: upload zone renderer
  const renderUploadZone = (category, label, accept = "image/*,video/*,.pdf") => {
    const files = uploads[category] || [];
    const categoryUploading = Object.entries(uploading).filter(([id]) => id.startsWith(category));

    return (
      <div className="mb-4">
        <p className={labelCls}>{label}</p>
        <div
          className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-sky-400 transition-colors cursor-pointer"
          onClick={() => {
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = true;
            input.accept = accept;
            input.onchange = (e) => handleFileUpload(category, Array.from(e.target.files));
            input.click();
          }}
        >
          <Upload className="mx-auto text-slate-400 mb-2" size={24} />
          <p className="text-sm text-slate-500">{t.s4_drop}</p>
        </div>
        {categoryUploading.length > 0 && (
          <div className="mt-2 space-y-1">
            {categoryUploading.map(([id, progress]) => (
              <div key={id} className="flex items-center gap-2 text-xs text-slate-500">
                <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <span>{progress}%</span>
              </div>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-600 truncate">{f.name}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button onClick={() => removeFile(category, f.id)} className="text-slate-400 hover:text-red-500 flex-shrink-0">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Helper: review row
  const reviewRow = (label, value, stepIdx) => (
    <div className="flex items-start justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-slate-700 mt-0.5">{value || "—"}</p>
      </div>
      <button
        onClick={() => goToStep(stepIdx)}
        className="ml-3 text-sky-500 hover:text-sky-600 flex-shrink-0"
        title={t.s5_edit}
      >
        <Edit3 size={14} />
      </button>
    </div>
  );

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen" data-testid="technical-support-request-page">
      <div className="max-w-[720px] mx-auto px-6 md:px-12">

        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-ui uppercase tracking-[0.15em] text-slate-500">
              {t.stepLabel} {currentStep + 1} {t.of} {totalSteps}
            </span>
            <span className="text-xs font-ui text-slate-400">{t.steps[currentStep]}</span>
          </div>
          <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between mt-3">
            {t.steps.map((step, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300 ${
                  i < currentStep ? "bg-sky-500 text-white" : i === currentStep ? "bg-sky-500 text-white ring-4 ring-sky-100" : "bg-slate-200 text-slate-400"
                }`}>
                  {i < currentStep ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span className={`text-[10px] mt-1 font-ui text-center ${i <= currentStep ? "text-slate-700" : "text-slate-400"}`}>
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white border border-slate-200 rounded-lg p-8 md:p-12 mb-8">

          {/* ===== STEP 0: Landing ===== */}
          {currentStep === 0 && (
            <div>
              <div className="overline mb-4">{t.overline}</div>
              <h1 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-4">{t.title}</h1>
              <p className="text-slate-600 text-base leading-relaxed mb-6">{t.subtitle}</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-slate-500 text-sm leading-relaxed">{t.helpText}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {t.checklist.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="text-sky-500 flex-shrink-0" size={18} />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={goNext}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  {t.ctaPrimary} <ChevronRight size={16} />
                </button>
                <a href="/support"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <ArrowLeft size={14} /> {t.ctaSecondary}
                </a>
              </div>
            </div>
          )}

          {/* ===== STEP 1: Eligibility / Pre-triage ===== */}
          {currentStep === 1 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s1_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s1_text}</p>

              {/* Q1: Issue type */}
              <div className="mb-6">
                <p className="text-sm font-medium text-slate-700 mb-3">{t.s1_q1}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { val: "technical", label: t.s1_q1_a1 },
                    { val: "warranty", label: t.s1_q1_a2 },
                    { val: "parts", label: t.s1_q1_a3 },
                    { val: "general", label: t.s1_q1_a4 },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      onClick={() => updateForm("q1", opt.val)}
                      className={`py-3 px-4 rounded-lg border text-sm font-medium transition-colors text-left ${
                        form.q1 === opt.val
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q2: Severity */}
              <div className="mb-6">
                <p className="text-sm font-medium text-slate-700 mb-3">{t.s1_q2}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { val: "unusable", label: t.s1_q2_a1 },
                    { val: "partial", label: t.s1_q2_a2 },
                    { val: "doubt", label: t.s1_q2_a3 },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      onClick={() => updateForm("q2", opt.val)}
                      className={`py-3 px-4 rounded-lg border text-sm font-medium transition-colors text-left ${
                        form.q2 === opt.val
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Redirect suggestion */}
              {redirectSuggestion && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-amber-800">{redirectSuggestion}</p>
                      <p className="text-xs text-amber-600 mt-1">{t.s1_continue_anyway}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 2: Product Identification ===== */}
          {currentStep === 2 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s2_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s2_text}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t.s2_type}</label>
                  <select className={selectCls} value={form.productType} onChange={(e) => updateForm("productType", e.target.value)}>
                    <option value="">{t.s2_type_ph}</option>
                    {t.s2_type_opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t.s2_model}</label>
                  <input className={inputCls} placeholder={t.s2_model_ph} value={form.model} onChange={(e) => updateForm("model", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t.s2_serial}</label>
                  <input className={inputCls} placeholder={t.s2_serial_ph} value={form.serial} onChange={(e) => updateForm("serial", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t.s2_variant}</label>
                  <input className={inputCls} placeholder={t.s2_variant_ph} value={form.variant} onChange={(e) => updateForm("variant", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t.s2_purchase_date}</label>
                  <input type="date" className={inputCls} value={form.purchaseDate} onChange={(e) => updateForm("purchaseDate", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t.s2_purchase_loc}</label>
                  <input className={inputCls} placeholder={t.s2_purchase_loc_ph} value={form.purchaseLoc} onChange={(e) => updateForm("purchaseLoc", e.target.value)} />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
                <HelpCircle className="text-slate-400 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-xs text-slate-500">{t.s2_help}</p>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Issue Description ===== */}
          {currentStep === 3 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s3_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s3_text}</p>

              <div className="mb-4">
                <label className={labelCls}>{t.s3_category}</label>
                <select className={selectCls} value={form.category} onChange={(e) => updateForm("category", e.target.value)}>
                  <option value="">{locale === "pt" ? "Selecione" : locale === "es" ? "Seleccione" : "Select"}</option>
                  {t.s3_cat_opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              <div className="mb-4">
                <label className={labelCls}>{t.s3_desc}</label>
                <textarea className={`${inputCls} min-h-[120px] resize-y`} placeholder={t.s3_desc_ph} value={form.desc} onChange={(e) => updateForm("desc", e.target.value)} />
                <div className="flex gap-4 mt-2">
                  <p className="text-[11px] text-slate-400 italic">{t.s3_example1}</p>
                  <p className="text-[11px] text-slate-400 italic">{t.s3_example2}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t.s3_when}</label>
                  <input type="date" className={inputCls} value={form.when} onChange={(e) => updateForm("when", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t.s3_context}</label>
                  <select className={selectCls} value={form.context} onChange={(e) => updateForm("context", e.target.value)}>
                    <option value="">{locale === "pt" ? "Selecione" : locale === "es" ? "Seleccione" : "Select"}</option>
                    {t.s3_ctx_opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-slate-700 mb-3">{t.s3_partial}</p>
                <div className="flex gap-2">
                  {[
                    { val: "yes", label: t.s3_partial_yes },
                    { val: "no", label: t.s3_partial_no },
                    { val: "na", label: t.s3_partial_na },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      onClick={() => updateForm("partial", opt.val)}
                      className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        form.partial === opt.val
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-slate-700 mb-3">{t.s3_frequency}</p>
                <div className="flex gap-2">
                  {[
                    { val: "intermittent", label: t.s3_freq_int },
                    { val: "constant", label: t.s3_freq_const },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      onClick={() => updateForm("frequency", opt.val)}
                      className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        form.frequency === opt.val
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className={labelCls}>{t.s3_tried}</label>
                <input className={inputCls} placeholder={t.s3_tried_ph} value={form.tried} onChange={(e) => updateForm("tried", e.target.value)} />
              </div>
            </div>
          )}

          {/* ===== STEP 4: Upload Evidence ===== */}
          {currentStep === 4 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s4_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s4_text}</p>

              {renderUploadZone("product", t.s4_product)}
              {renderUploadZone("serial", t.s4_serial)}
              {renderUploadZone("defect", t.s4_defect)}
              {renderUploadZone("video", t.s4_video, "video/*")}
              {renderUploadZone("receipt", t.s4_receipt, "image/*,.pdf")}
              {renderUploadZone("other", t.s4_other)}

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2 mt-4">
                <HelpCircle className="text-slate-400 flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <p className="text-xs text-slate-500">{t.s4_help}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{t.s4_formats}</p>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 5: Review ===== */}
          {currentStep === 5 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s5_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s5_text}</p>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="text-xs font-ui uppercase tracking-wider text-slate-500 mb-2">{t.s5_eligibility}</h3>
                {reviewRow(t.s1_q1, form.q1 === "technical" ? t.s1_q1_a1 : form.q1 === "warranty" ? t.s1_q1_a2 : form.q1 === "parts" ? t.s1_q1_a3 : form.q1 === "general" ? t.s1_q1_a4 : "", 1)}
                {reviewRow(t.s1_q2, form.q2 === "unusable" ? t.s1_q2_a1 : form.q2 === "partial" ? t.s1_q2_a2 : form.q2 === "doubt" ? t.s1_q2_a3 : "", 1)}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="text-xs font-ui uppercase tracking-wider text-slate-500 mb-2">{t.s5_product}</h3>
                {reviewRow(t.s2_type, form.productType, 2)}
                {reviewRow(t.s2_model, form.model, 2)}
                {reviewRow(t.s5_serial, form.serial, 2)}
                {reviewRow(t.s2_variant, form.variant, 2)}
                {reviewRow(t.s5_purchase, form.purchaseDate || form.purchaseLoc, 2)}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="text-xs font-ui uppercase tracking-wider text-slate-500 mb-2">{t.s5_issue}</h3>
                {reviewRow(t.s5_category, form.category, 3)}
                {reviewRow(t.s5_desc, form.desc, 3)}
                {reviewRow(t.s5_when, form.when, 3)}
                {reviewRow(t.s5_context, form.context, 3)}
                {reviewRow(t.s5_partial, form.partial === "yes" ? t.s3_partial_yes : form.partial === "no" ? t.s3_partial_no : form.partial === "na" ? t.s3_partial_na : "", 3)}
                {reviewRow(t.s5_frequency, form.frequency === "intermittent" ? t.s3_freq_int : form.frequency === "constant" ? t.s3_freq_const : "", 3)}
                {reviewRow(t.s5_tried, form.tried, 3)}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h3 className="text-xs font-ui uppercase tracking-wider text-slate-500 mb-2">{t.s5_attachments}</h3>
                <p className="text-sm text-slate-700">
                  {totalUploaded > 0
                    ? `${totalUploaded} ${locale === "pt" ? "arquivo(s) anexado(s)" : locale === "es" ? "archivo(s) adjunto(s)" : "file(s) attached"}`
                    : (locale === "pt" ? "Nenhum anexo" : locale === "es" ? "Sin adjuntos" : "No attachments")}
                </p>
                <button onClick={() => goToStep(4)} className="text-sky-500 text-xs mt-1 hover:underline">{t.s5_edit}</button>
              </div>

              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.terms}
                    onChange={(e) => updateForm("terms", e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500"
                  />
                  <span className="text-sm text-slate-600">{t.s5_terms}</span>
                </label>
              </div>
            </div>
          )}

          {/* ===== STEP 6: Confirmation ===== */}
          {currentStep === 6 && protocol && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-emerald-500" size={32} />
              </div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s6_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s6_text}</p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                <p className="text-xs font-ui uppercase tracking-wider text-slate-500 mb-2">{t.s6_protocol}</p>
                <p className="text-2xl font-mono font-bold text-slate-900">{protocol}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-left">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">{t.s6_status}</p>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
                    <span className="text-sm font-medium text-slate-700">{t.s6_status_val}</span>
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">{t.s6_eta}</p>
                  <p className="text-sm font-medium text-slate-700">{t.s6_eta_val}</p>
                </div>
              </div>

              <p className="text-sm text-slate-500 mb-6">{t.s6_secondary}</p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`/support/technical-request/${protocol}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <Eye size={16} /> {t.s6_view}
                </a>
                <a href="/support/technical-request"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <Plus size={14} /> {t.s6_new}
                </a>
                <a href="/support"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <ArrowLeft size={14} /> {t.s6_support}
                </a>
              </div>
            </div>
          )}

        </div>

        {/* Navigation buttons for steps > 0 */}
        {currentStep > 0 && currentStep < 6 && (
          <div className="flex items-center justify-between mb-8">
            <button onClick={goBack}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
              <ChevronLeft size={16} /> {t.back}
            </button>
            {currentStep === 5 ? (
              <button
                onClick={handleSubmit}
                disabled={!form.terms}
                className={`inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-ui font-semibold uppercase tracking-wider transition-colors ${
                  form.terms
                    ? "bg-sky-500 hover:bg-sky-600 text-white"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                {t.s5_send} <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                {t.next} <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}

        {/* Help Block */}
        {currentStep < 6 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="text-sky-500" size={20} />
              <h3 className="font-semibold text-slate-900">{locale === "pt" ? "Dicas para acelerar a análise" : locale === "es" ? "Consejos para acelerar el análisis" : "Tips to speed up analysis"}</h3>
            </div>
            <ul className="space-y-3 mb-4">
              {t.checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <CheckCircle className="text-sky-500 mt-0.5 flex-shrink-0" size={16} />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Clock size={14} />
              <span>{locale === "pt" ? "Tempo estimado: 3–5 minutos" : locale === "es" ? "Tiempo estimado: 3–5 minutos" : "Estimated time: 3–5 minutes"}</span>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}
